import React, { useState } from "react";
import { FileText, Calendar } from "lucide-react";
import { useAuth } from "../../../context/Auth";
import { toast } from "react-toastify";

const IncomeStatement = () => {
  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchIncomeStatement = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await requestWithAuth(
        `/report/income-statement/${startDate}/${endDate}`,
        "GET"
      );
      setIncomeStatement(data);
    } catch (error) {
      toast.error("Failed to get income statement");
      setIncomeStatement(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchIncomeStatement(e);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Reports</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Income Statement
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <FileText size={32} className="text-blue-500" />
                Income Statement
              </h1>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Date Selection Form */}
          <div className="p-6 border-b border-gray-200">
            <form onSubmit={handleSubmit}>
              <div className="flex items-end gap-4 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Start Date
                  </label>
                  <div className="relative">
                    <Calendar
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="date"
                      required
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-[200px]">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    End Date
                  </label>
                  <div className="relative">
                    <Calendar
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="date"
                      required
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !startDate || !endDate}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2.5 rounded-lg font-medium transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FileText size={18} />
                      Get Statement
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Statement Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex items-center gap-3 text-gray-600">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span>Loading statement...</span>
                </div>
              </div>
            ) : incomeStatement ? (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Item
                      </th>
                      <th className="text-right py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider w-32">
                        GHS
                      </th>
                      <th className="text-right py-4 px-6 text-xs font-semibold text-gray-600 uppercase tracking-wider w-32">
                        GHS
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Income Section */}
                    <tr className="bg-blue-50">
                      <td
                        colSpan={3}
                        className="py-3 px-6 font-bold text-sm text-blue-900 uppercase"
                      >
                        Income
                      </td>
                    </tr>
                    {incomeStatement?.income_list?.map((income, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-3 px-6 text-sm text-gray-900">
                          {income?.narration}
                        </td>
                        <td className="py-3 px-6 text-sm text-gray-600 text-right">
                          —
                        </td>
                        <td className="py-3 px-6 text-sm text-gray-900 text-right font-medium">
                          {income?.total_amount}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 border-t-2 border-gray-300">
                      <td
                        colSpan={2}
                        className="py-3 px-6 font-bold text-sm text-gray-900"
                      >
                        Total Income
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-900 text-right font-bold">
                        {incomeStatement?.total_income}
                      </td>
                    </tr>

                    {/* Expense Section */}
                    <tr className="bg-red-50">
                      <td
                        colSpan={3}
                        className="py-3 px-6 font-bold text-sm text-red-900 uppercase"
                      >
                        Expense
                      </td>
                    </tr>
                    {incomeStatement?.expense_list?.map((expense, index) => (
                      <tr
                        key={index}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-3 px-6 text-sm text-gray-900 capitalize">
                          {expense?.narration}
                        </td>
                        <td className="py-3 px-6 text-sm text-gray-900 text-right font-medium">
                          {expense?.total_amount}
                        </td>
                        <td className="py-3 px-6 text-sm text-gray-600 text-right">
                          —
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 border-t-2 border-gray-300">
                      <td
                        colSpan={2}
                        className="py-3 px-6 font-bold text-sm text-gray-900"
                      >
                        Total Expense
                      </td>
                      <td className="py-3 px-6 text-sm text-gray-900 text-right font-bold">
                        {incomeStatement?.total_expenses}
                      </td>
                    </tr>

                    {/* Net Income */}
                    <tr className="bg-green-50 border-t-2 border-green-300">
                      <td
                        colSpan={2}
                        className="py-4 px-6 font-bold text-base text-green-900"
                      >
                        Net Income
                      </td>
                      <td className="py-4 px-6 text-base text-green-900 text-right font-bold">
                        {incomeStatement?.net_income}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-20 text-center">
                <div className="text-gray-400 mb-2">
                  <svg
                    className="mx-auto h-12 w-12"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <p className="text-gray-500 text-sm font-medium">
                  No statement generated
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  Please select start and end dates to generate the income
                  statement
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeStatement;
